import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './login.css';
import logo from '../../assets/logo.png';

const Login = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setError('');
    setIsLoading(true);

    if (!formData.username || !formData.password) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/token', {
        method: 'POST',
        body: new URLSearchParams({
          username: formData.username,
          password: formData.password,
        }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      if (response.status === 403) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Compte désactivé');
      }
      if (!response.ok) {
        throw new Error("Nom d'utilisateur ou mot de passe invalide");
      }

      const data = await response.json();

      // Store the token and user data in localStorage
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('user_role', data.role);
      localStorage.setItem('user_id', data.userId);
      localStorage.setItem('stats_organisation', data.stats_organisation ? 'true' : 'false');
      // Le rôle dit le métier, l'organisation dit pour qui : le front a besoin
      // des deux. `is_staff` remplace l'ancien test sur le libellé du rôle.
      localStorage.setItem('organisation_id', data.organisation_id ?? '');
      localStorage.setItem('organisation_name', data.organisation_name ?? '');
      localStorage.setItem('is_staff', data.is_staff ? 'true' : 'false');
      localStorage.setItem('username', formData.username);

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Unable to connect to the server. Please try again later.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const missingUser = submitted && !formData.username;
  const missingPass = submitted && !formData.password;

  const FieldError = ({ id, children }) => (
    <div className="invalid-feedback" id={id} role="alert">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
        <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
      </svg>
      {children}
    </div>
  );

  return (
    <div className="login-page">
      <a className="login-brand" href="/" aria-label="CheckAI">
        <img src={logo} alt="CheckAI" />
      </a>

      <div className="login-layout">
        {/* Panneau gauche : scène de détection animée */}
        <div className="left-panel" aria-hidden="true">
          <div className="lg-aurora lg-aurora--a"></div>
          <div className="lg-aurora lg-aurora--b"></div>
          <div className="lg-particles">
              <span className="lg-particle" style={{ "--x": "8%", "--y": "22%", "--s": "3px", "--d": "9s", "--t": "0s" }} />
              <span className="lg-particle" style={{ "--x": "18%", "--y": "70%", "--s": "4px", "--d": "12s", "--t": "2s" }} />
              <span className="lg-particle" style={{ "--x": "30%", "--y": "35%", "--s": "2px", "--d": "10s", "--t": "1s" }} />
              <span className="lg-particle" style={{ "--x": "42%", "--y": "82%", "--s": "3px", "--d": "14s", "--t": "4s" }} />
              <span className="lg-particle" style={{ "--x": "55%", "--y": "18%", "--s": "2px", "--d": "11s", "--t": "3s" }} />
              <span className="lg-particle" style={{ "--x": "66%", "--y": "60%", "--s": "4px", "--d": "13s", "--t": "5s" }} />
              <span className="lg-particle" style={{ "--x": "76%", "--y": "28%", "--s": "3px", "--d": "9s", "--t": "2s" }} />
              <span className="lg-particle" style={{ "--x": "86%", "--y": "74%", "--s": "2px", "--d": "12s", "--t": "6s" }} />
              <span className="lg-particle" style={{ "--x": "92%", "--y": "40%", "--s": "3px", "--d": "10s", "--t": "1s" }} />
              <span className="lg-particle" style={{ "--x": "12%", "--y": "48%", "--s": "2px", "--d": "15s", "--t": "7s" }} />
              <span className="lg-particle" style={{ "--x": "48%", "--y": "50%", "--s": "2px", "--d": "8s", "--t": "3s" }} />
              <span className="lg-particle" style={{ "--x": "70%", "--y": "88%", "--s": "3px", "--d": "11s", "--t": "0s" }} />
              <span className="lg-particle" style={{ "--x": "25%", "--y": "10%", "--s": "2px", "--d": "13s", "--t": "5s" }} />
              <span className="lg-particle" style={{ "--x": "82%", "--y": "12%", "--s": "3px", "--d": "12s", "--t": "4s" }} />
          </div>

          <div className="ai-animation-container">
            <div className="ai-header">
              <h1>AI Damage Detection</h1>
              <p>Plateforme intelligente de détection et analyse des dommages</p>
            </div>

            <div className="lg-scene">
              <div className="lg-rings">
                <span></span><span></span><span></span>
              </div>

<svg className="lg-car" viewBox="0 0 480 220" fill="none">
                <defs>
                  <linearGradient id="lgBody" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#60a5fa" />
                    <stop offset="0.55" stopColor="#818cf8" />
                    <stop offset="1" stopColor="#c084fc" />
                  </linearGradient>
                  <linearGradient id="lgPaint" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#a5b4fc" />
                    <stop offset="0.14" stopColor="#6272dc" />
                    <stop offset="0.4" stopColor="#3140a0" />
                    <stop offset="0.68" stopColor="#1a2258" />
                    <stop offset="0.9" stopColor="#0b1030" />
                    <stop offset="1" stopColor="#1b2670" />
                  </linearGradient>
                  <linearGradient id="lgShade" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#000" stopOpacity="0.5" />
                    <stop offset="0.15" stopColor="#000" stopOpacity="0" />
                    <stop offset="0.85" stopColor="#000" stopOpacity="0" />
                    <stop offset="1" stopColor="#000" stopOpacity="0.32" />
                  </linearGradient>
                  <linearGradient id="lgSheen" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#fff" stopOpacity="0" />
                    <stop offset="0.5" stopColor="#e0e7ff" stopOpacity="0.2" />
                    <stop offset="1" stopColor="#fff" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="lgShoulder" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#fff" stopOpacity="0" />
                    <stop offset="0.22" stopColor="#e0e7ff" stopOpacity="0.8" />
                    <stop offset="0.7" stopColor="#fff" stopOpacity="0.6" />
                    <stop offset="1" stopColor="#fff" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="lgGlass" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#3b4f96" />
                    <stop offset="0.5" stopColor="#141d45" />
                    <stop offset="1" stopColor="#070b1c" />
                  </linearGradient>
                  <linearGradient id="lgSky" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#e0e7ff" stopOpacity="0.5" />
                    <stop offset="1" stopColor="#e0e7ff" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="lgChrome" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#f8fafc" />
                    <stop offset="0.45" stopColor="#8b9ab3" />
                    <stop offset="0.7" stopColor="#e2e8f0" />
                    <stop offset="1" stopColor="#64748b" />
                  </linearGradient>
                  <radialGradient id="lgRim" cx="0.4" cy="0.35" r="0.8">
                    <stop offset="0" stopColor="#f8fafc" />
                    <stop offset="0.6" stopColor="#94a3b8" />
                    <stop offset="1" stopColor="#334155" />
                  </radialGradient>
                  <radialGradient id="lgDisc" cx="0.5" cy="0.5" r="0.5">
                    <stop offset="0.3" stopColor="#3a4266" />
                    <stop offset="1" stopColor="#1c2240" />
                  </radialGradient>
                  <radialGradient id="lgTire" cx="0.5" cy="0.5" r="0.5">
                    <stop offset="0.72" stopColor="#090b13" />
                    <stop offset="1" stopColor="#1d2336" />
                  </radialGradient>
                  <linearGradient id="lgTireHi" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#a5b4cc" stopOpacity="0.8" />
                    <stop offset="0.5" stopColor="#94a3b8" stopOpacity="0" />
                    <stop offset="1" stopColor="#94a3b8" stopOpacity="0.25" />
                  </linearGradient>
                  <linearGradient id="lgLamp" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#fffbeb" />
                    <stop offset="0.5" stopColor="#fde68a" />
                    <stop offset="1" stopColor="#b45309" />
                  </linearGradient>
                  <linearGradient id="lgTailLamp" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#991b1b" />
                    <stop offset="1" stopColor="#fb7185" />
                  </linearGradient>
                  <linearGradient id="lgRocker" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#04060d" stopOpacity="0" />
                    <stop offset="1" stopColor="#04060d" stopOpacity="0.9" />
                  </linearGradient>
                  <linearGradient id="lgBeam" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#60a5fa" stopOpacity="0" />
                    <stop offset="1" stopColor="#93c5fd" stopOpacity="0.55" />
                  </linearGradient>
                  <radialGradient id="lgHead" cx="0.5" cy="0.5" r="0.5">
                    <stop offset="0" stopColor="#fef3c7" />
                    <stop offset="1" stopColor="#fbbf24" stopOpacity="0" />
                  </radialGradient>
                  <linearGradient id="lgRefFade" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#fff" stopOpacity="0.55" />
                    <stop offset="1" stopColor="#fff" stopOpacity="0" />
                  </linearGradient>
                  <mask id="lgRefMask" maskUnits="userSpaceOnUse" x="0" y="183" width="480" height="37">
                    <rect x="0" y="183" width="480" height="37" fill="url(#lgRefFade)" />
                  </mask>
                  <filter id="lgBlur" x="-2%" y="-2%" width="104%" height="104%"><feGaussianBlur stdDeviation="1.1" /></filter>
                  <pattern id="lgLines" width="6" height="4" patternUnits="userSpaceOnUse">
                    <rect width="6" height="1" fill="#bfdbfe" opacity="0.08" />
                    <animateTransform attributeName="patternTransform" type="translate" from="0 0" to="0 4" dur="1.2s" repeatCount="indefinite" />
                  </pattern>
                  <clipPath id="lgClip">
                    <path d="M24 144 C21 130 21 119 27 112 C33 106 50 103 72 100 L112 96 C132 74 160 56 204 50 L262 48 C300 48 334 62 358 90 L368 94 C404 98 436 103 452 112 C459 117 460 128 459 140 L458 152 L450 160 L412.9 160 A38 38 0 1 0 339.1 160 L140.9 160 A38 38 0 1 0 67.1 160 L40 160 L28 154 Z" />
                  </clipPath>
                </defs>

                <ellipse className="lg-ground" cx="240" cy="186" rx="218" ry="8" />
                <ellipse className="lg-underglow" cx="240" cy="181" rx="180" ry="7" />

                <g mask="url(#lgRefMask)" filter="url(#lgBlur)">
                  <use href="#lgCar" transform="translate(0 366) scale(1 -1)" />
                </g>
                <ellipse cx="104" cy="183" rx="36" ry="3" fill="#000" opacity="0.75" />
                <ellipse cx="376" cy="183" rx="36" ry="3" fill="#000" opacity="0.75" />

                <g className="lg-car-body">
                  <g id="lgCar">
                  <circle cx="104" cy="151" r="39" fill="#03050b" />
                  <circle cx="376" cy="151" r="39" fill="#03050b" />

                  <path d="M24 144 C21 130 21 119 27 112 C33 106 50 103 72 100 L112 96 C132 74 160 56 204 50 L262 48 C300 48 334 62 358 90 L368 94 C404 98 436 103 452 112 C459 117 460 128 459 140 L458 152 L450 160 L412.9 160 A38 38 0 1 0 339.1 160 L140.9 160 A38 38 0 1 0 67.1 160 L40 160 L28 154 Z" fill="url(#lgPaint)" />
                  <g clipPath="url(#lgClip)">
                    <rect x="15" y="40" width="450" height="125" fill="url(#lgShade)" />
                    <path d="M120 40 L215 40 L150 170 L55 170 Z" fill="url(#lgSheen)" />
                    <path d="M290 40 L330 40 L270 170 L230 170 Z" fill="url(#lgSheen)" opacity="0.55" />
                    <rect x="15" y="138" width="450" height="26" fill="url(#lgRocker)" />
                    <path d="M40 158 L450 158" stroke="#7c8cf0" strokeWidth="1" opacity="0.55" />
                    <path d="M44 107 C180 98 330 98 452 113" stroke="url(#lgShoulder)" strokeWidth="2.4" strokeLinecap="round" />
                    <path d="M44 110 C180 102 330 102 452 116" stroke="#04061a" strokeWidth="1.8" opacity="0.55" />
                    <path d="M56 138 C190 130 320 130 440 138" stroke="#c7d2fe" strokeWidth="0.9" opacity="0.3" />
                    <path d="M56 141 C190 133 320 133 440 141" stroke="#04061a" strokeWidth="1.2" opacity="0.45" />
                    <path d="M118 97 C140 74 162 57 204 51 L262 49 C300 49 332 63 356 90" stroke="#eef2ff" strokeWidth="1.5" opacity="0.8" strokeLinecap="round" />
                    <path d="M368 96 C404 100 436 105 451 114" stroke="#fff" strokeWidth="1.4" opacity="0.55" strokeLinecap="round" />
                    <path d="M72 101 L112 97" stroke="#fff" strokeWidth="1.2" opacity="0.5" strokeLinecap="round" />
                  </g>
                  <path d="M24 144 C21 130 21 119 27 112 C33 106 50 103 72 100 L112 96 C132 74 160 56 204 50 L262 48 C300 48 334 62 358 90 L368 94 C404 98 436 103 452 112 C459 117 460 128 459 140 L458 152 L450 160 L412.9 160 A38 38 0 1 0 339.1 160 L140.9 160 A38 38 0 1 0 67.1 160 L40 160 L28 154 Z" fill="url(#lgLines)" />

                  <clipPath id="lgGlassClip"><path d="M120 96 C140 76 166 60 206 56 L236 55 L238 96 Z" /><path d="M246 55 L264 55 C298 55 326 67 348 94 L246 96 Z" /></clipPath>
                  <path d="M120 96 C140 76 166 60 206 56 L236 55 L238 96 Z" fill="url(#lgGlass)" />
                  <path d="M246 55 L264 55 C298 55 326 67 348 94 L246 96 Z" fill="url(#lgGlass)" />
                  <g clipPath="url(#lgGlassClip)">
                    <g fill="#0a0f26" opacity="0.85">
                      <ellipse cx="196" cy="74" rx="7" ry="9.5" />
                      <path d="M184 96 C184 84 190 80 200 80 C210 80 212 88 212 96 Z" />
                      <ellipse cx="290" cy="74" rx="7" ry="9.5" />
                      <path d="M278 96 C278 84 284 80 294 80 C304 80 306 88 306 96 Z" />
                      <ellipse cx="146" cy="82" rx="3" ry="6" />
                    </g>
                    <path d="M138 96 L186 55 L212 55 L170 96 Z" fill="url(#lgSky)" />
                    <path d="M252 96 L292 55 L304 56 L276 96 Z" fill="url(#lgSky)" opacity="0.8" />
                    <path d="M120 96 L348 96 L348 86 L120 90 Z" fill="#6d7fe0" opacity="0.22" />
                  </g>
                  <path d="M120 96 C140 76 166 60 206 56 L236 55 L238 96 Z" stroke="#cbd5e1" strokeWidth="0.9" strokeLinejoin="round" />
                  <path d="M246 55 L264 55 C298 55 326 67 348 94 L246 96 Z" stroke="#cbd5e1" strokeWidth="0.9" strokeLinejoin="round" />
                  <path d="M119 97.5 L348 96.5" stroke="url(#lgChrome)" strokeWidth="1.4" />
                  <path d="M236 54 L246 54 L247 97 L237 97 Z" fill="#05081a" />

                  <path d="M153 97 L150 146 M241 97 L241 146 M334 96 L339 146" stroke="#04061a" strokeWidth="1.1" opacity="0.7" />
                  <path d="M154.2 98 L151.2 146 M242.2 98 L242.2 146 M335.2 97 L340.2 146" stroke="#c7d2fe" strokeWidth="0.5" opacity="0.25" />
                  <rect x="122" y="108" width="12" height="6" rx="1.5" stroke="#04061a" strokeWidth="0.6" opacity="0.6" />

                  <rect x="196" y="107" width="22" height="3.8" rx="1.9" fill="url(#lgChrome)" />
                  <rect x="196" y="110.6" width="22" height="1.3" rx="0.6" fill="#04061a" opacity="0.55" />
                  <rect x="266" y="107" width="22" height="3.8" rx="1.9" fill="url(#lgChrome)" />
                  <rect x="266" y="110.6" width="22" height="1.3" rx="0.6" fill="#04061a" opacity="0.55" />

                  <path d="M337 93 L354 91 L359 99 C353 103 344 103 339 100 Z" fill="url(#lgPaint)" stroke="#04061a" strokeWidth="0.6" strokeLinejoin="round" />
                  <path d="M341 94.5 L354 93" stroke="#fff" strokeWidth="0.9" opacity="0.65" />
                  <path d="M344 99 L354 98" stroke="#04061a" strokeWidth="1" opacity="0.5" />

                  <path className="lg-tail" d="M22 112 C25 107 33 105 44 104 L48 104.5 L48 117 L24 122 C22 119 21 116 22 112 Z" fill="url(#lgTailLamp)" stroke="#fecaca" strokeWidth="0.5" />
                  <path d="M25 113 L46 108.5 M25 117 L46 113" stroke="#fff" strokeWidth="0.9" strokeLinecap="round" opacity="0.65" />
                  <rect x="24" y="136" width="8" height="4" rx="1.5" fill="#ef4444" opacity="0.85" />
                  <rect x="24" y="128" width="12" height="3" rx="1" fill="#05070f" />

                  <path d="M418 106 C438 107 453 111 458 118 L459 125 C448 125 432 120 418 113 Z" fill="#04060f" stroke="#e2e8f0" strokeWidth="0.8" />
                  <path className="lg-head" d="M424 109 C440 110 452 113.5 456 118.5 L456.5 122 C446 121 434 117.5 424 112.5 Z" fill="url(#lgLamp)" />
                  <circle cx="440" cy="114.5" r="3.4" fill="#fff" opacity="0.9" />
                  <circle cx="440" cy="114.5" r="5" stroke="#cbd5e1" strokeWidth="0.6" opacity="0.8" />
                  <path d="M420 113 C434 118 448 121 458 122" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
                  <ellipse className="lg-head-glow" cx="455" cy="117" rx="28" ry="15" fill="url(#lgHead)" />
                  <path d="M432 131 L458 129 L458 150 L428 152 Z" fill="#04060d" stroke="#334155" strokeWidth="0.8" />
                  <path d="M436 135 L456 133.5 M435 139 L456 137.5 M434 143 L456 141.5 M433 147 L456 145.5" stroke="#64748b" strokeWidth="0.9" opacity="0.85" />
                  <path d="M400 128 C410 128 418 130 424 133" stroke="#04061a" strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
                  <rect x="424" y="133" width="6" height="6" rx="3" fill="#fde68a" opacity="0.8" />

                  <path d="M24 144 C21 130 21 119 27 112 C33 106 50 103 72 100 L112 96 C132 74 160 56 204 50 L262 48 C300 48 334 62 358 90 L368 94 C404 98 436 103 452 112 C459 117 460 128 459 140 L458 152 L450 160 L412.9 160 A38 38 0 1 0 339.1 160 L140.9 160 A38 38 0 1 0 67.1 160 L40 160 L28 154 Z" stroke="url(#lgBody)" strokeWidth="1.1" strokeLinejoin="round" opacity="0.7" />

                  <g>
                    <circle cx="104" cy="151" r="32" fill="url(#lgTire)" />
                    <circle cx="104" cy="151" r="31.2" stroke="url(#lgTireHi)" strokeWidth="1.4" />
                    <circle cx="104" cy="151" r="28" stroke="#2a3248" strokeWidth="0.7" opacity="0.9" />
                    <circle cx="104" cy="151" r="26" stroke="#1a2036" strokeWidth="0.6" />
                    <circle cx="104" cy="151" r="24" fill="#080b18" />
                    <circle cx="104" cy="151" r="23" fill="url(#lgRim)" />
                    <circle cx="104" cy="151" r="21.4" fill="#0a0e1f" />
                    <circle cx="104" cy="151" r="18.2" fill="url(#lgDisc)" stroke="#5a6488" strokeWidth="0.6" />
                    <g stroke="#0a0e1f" strokeWidth="0.8" opacity="0.85"><line x1="113.0" y1="151.0" x2="121.5" y2="151.0" /><line x1="112.2" y1="154.7" x2="120.0" y2="158.1" /><line x1="110.0" y1="157.7" x2="115.7" y2="164.0" /><line x1="106.8" y1="159.6" x2="109.4" y2="167.6" /><line x1="103.1" y1="160.0" x2="102.2" y2="168.4" /><line x1="99.5" y1="158.8" x2="95.2" y2="166.2" /><line x1="96.7" y1="156.3" x2="89.8" y2="161.3" /><line x1="95.2" y1="152.9" x2="86.9" y2="154.6" /><line x1="95.2" y1="149.1" x2="86.9" y2="147.4" /><line x1="96.7" y1="145.7" x2="89.8" y2="140.7" /><line x1="99.5" y1="143.2" x2="95.2" y2="135.8" /><line x1="103.1" y1="142.0" x2="102.2" y2="133.6" /><line x1="106.8" y1="142.4" x2="109.4" y2="134.4" /><line x1="110.0" y1="144.3" x2="115.7" y2="138.0" /><line x1="112.2" y1="147.3" x2="120.0" y2="143.9" /></g>
                    <path d="M108 131 A20.5 20.5 0 0 1 122 145" stroke="#dc2626" strokeWidth="4" strokeLinecap="round" />
                    <path d="M109 132 A19.5 19.5 0 0 1 117 136.5" stroke="#fca5a5" strokeWidth="0.8" strokeLinecap="round" opacity="0.8" />
                    <g className="lg-spokes">
                      <g fill="url(#lgChrome)" stroke="#e2e8f0" strokeWidth="0.35" strokeLinejoin="round"><path d="M102.2 145.3 L100.7 130.8 L98.3 131.3 L101.5 145.5 Z" /><path d="M105.8 145.3 L107.3 130.8 L109.7 131.3 L106.5 145.5 Z" /><path d="M108.9 147.5 L122.2 141.6 L121.0 139.5 L108.4 146.9 Z" /><path d="M110.0 150.9 L124.3 147.9 L124.5 150.3 L110.0 151.7 Z" /><path d="M108.8 154.6 L118.5 165.5 L120.2 163.6 L109.2 153.9 Z" /><path d="M105.9 156.7 L113.3 169.3 L111.0 170.3 L105.2 156.9 Z" /><path d="M102.1 156.7 L94.7 169.3 L97.0 170.3 L102.8 156.9 Z" /><path d="M99.2 154.6 L89.5 165.5 L87.8 163.6 L98.8 153.9 Z" /><path d="M98.0 150.9 L83.7 147.9 L83.5 150.3 L98.0 151.7 Z" /><path d="M99.1 147.5 L85.8 141.6 L87.0 139.5 L99.6 146.9 Z" /></g>
                      <circle cx="104" cy="151" r="6.8" fill="url(#lgChrome)" stroke="#0a0e1f" strokeWidth="0.6" />
                      <g fill="#0a0e1f"><circle cx="104.0" cy="147.4" r="0.9" /><circle cx="107.4" cy="149.9" r="0.9" /><circle cx="106.1" cy="153.9" r="0.9" /><circle cx="101.9" cy="153.9" r="0.9" /><circle cx="100.6" cy="149.9" r="0.9" /></g>
                    </g>
                    <path d="M85 138 A23 23 0 0 1 106 128" stroke="#fff" strokeWidth="1.1" strokeLinecap="round" opacity="0.5" />
                    <circle cx="104" cy="151" r="2.6" fill="#cbd5e1" />
                    <circle cx="104" cy="151" r="1" fill="#475569" />
                  </g>
                  <g>
                    <circle cx="376" cy="151" r="32" fill="url(#lgTire)" />
                    <circle cx="376" cy="151" r="31.2" stroke="url(#lgTireHi)" strokeWidth="1.4" />
                    <circle cx="376" cy="151" r="28" stroke="#2a3248" strokeWidth="0.7" opacity="0.9" />
                    <circle cx="376" cy="151" r="26" stroke="#1a2036" strokeWidth="0.6" />
                    <circle cx="376" cy="151" r="24" fill="#080b18" />
                    <circle cx="376" cy="151" r="23" fill="url(#lgRim)" />
                    <circle cx="376" cy="151" r="21.4" fill="#0a0e1f" />
                    <circle cx="376" cy="151" r="18.2" fill="url(#lgDisc)" stroke="#5a6488" strokeWidth="0.6" />
                    <g stroke="#0a0e1f" strokeWidth="0.8" opacity="0.85"><line x1="385.0" y1="151.0" x2="393.5" y2="151.0" /><line x1="384.2" y1="154.7" x2="392.0" y2="158.1" /><line x1="382.0" y1="157.7" x2="387.7" y2="164.0" /><line x1="378.8" y1="159.6" x2="381.4" y2="167.6" /><line x1="375.1" y1="160.0" x2="374.2" y2="168.4" /><line x1="371.5" y1="158.8" x2="367.2" y2="166.2" /><line x1="368.7" y1="156.3" x2="361.8" y2="161.3" /><line x1="367.2" y1="152.9" x2="358.9" y2="154.6" /><line x1="367.2" y1="149.1" x2="358.9" y2="147.4" /><line x1="368.7" y1="145.7" x2="361.8" y2="140.7" /><line x1="371.5" y1="143.2" x2="367.2" y2="135.8" /><line x1="375.1" y1="142.0" x2="374.2" y2="133.6" /><line x1="378.8" y1="142.4" x2="381.4" y2="134.4" /><line x1="382.0" y1="144.3" x2="387.7" y2="138.0" /><line x1="384.2" y1="147.3" x2="392.0" y2="143.9" /></g>
                    <path d="M380 131 A20.5 20.5 0 0 1 394 145" stroke="#dc2626" strokeWidth="4" strokeLinecap="round" />
                    <path d="M381 132 A19.5 19.5 0 0 1 389 136.5" stroke="#fca5a5" strokeWidth="0.8" strokeLinecap="round" opacity="0.8" />
                    <g className="lg-spokes">
                      <g fill="url(#lgChrome)" stroke="#e2e8f0" strokeWidth="0.35" strokeLinejoin="round"><path d="M374.2 145.3 L372.7 130.8 L370.3 131.3 L373.5 145.5 Z" /><path d="M377.8 145.3 L379.3 130.8 L381.7 131.3 L378.5 145.5 Z" /><path d="M380.9 147.5 L394.2 141.6 L393.0 139.5 L380.4 146.9 Z" /><path d="M382.0 150.9 L396.3 147.9 L396.5 150.3 L382.0 151.7 Z" /><path d="M380.8 154.6 L390.5 165.5 L392.2 163.6 L381.2 153.9 Z" /><path d="M377.9 156.7 L385.3 169.3 L383.0 170.3 L377.2 156.9 Z" /><path d="M374.1 156.7 L366.7 169.3 L369.0 170.3 L374.8 156.9 Z" /><path d="M371.2 154.6 L361.5 165.5 L359.8 163.6 L370.8 153.9 Z" /><path d="M370.0 150.9 L355.7 147.9 L355.5 150.3 L370.0 151.7 Z" /><path d="M371.1 147.5 L357.8 141.6 L359.0 139.5 L371.6 146.9 Z" /></g>
                      <circle cx="376" cy="151" r="6.8" fill="url(#lgChrome)" stroke="#0a0e1f" strokeWidth="0.6" />
                      <g fill="#0a0e1f"><circle cx="376.0" cy="147.4" r="0.9" /><circle cx="379.4" cy="149.9" r="0.9" /><circle cx="378.1" cy="153.9" r="0.9" /><circle cx="373.9" cy="153.9" r="0.9" /><circle cx="372.6" cy="149.9" r="0.9" /></g>
                    </g>
                    <path d="M357 138 A23 23 0 0 1 378 128" stroke="#fff" strokeWidth="1.1" strokeLinecap="round" opacity="0.5" />
                    <circle cx="376" cy="151" r="2.6" fill="#cbd5e1" />
                    <circle cx="376" cy="151" r="1" fill="#475569" />
                  </g>
                </g>
                </g>

                <g clipPath="url(#lgClip)">
                  <rect className="lg-beam" x="-70" y="30" width="70" height="130" fill="url(#lgBeam)" />
                </g>
                <rect className="lg-beam-line" x="-2" y="30" width="2.5" height="150" rx="1.2" />

                <g className="lg-box lg-box--1">
                  <rect x="162" y="98" width="72" height="46" rx="3" />
                  <g className="lg-tag"><rect x="162" y="81" width="68" height="16" rx="4" /><text x="169" y="92.5">Corrosion</text></g>
                </g>
                <g className="lg-box lg-box--2">
                  <rect x="344" y="94" width="78" height="34" rx="3" />
                  <g className="lg-tag"><rect x="344" y="77" width="58" height="16" rx="4" /><text x="351" y="88.5">Fissures</text></g>
                </g>
                <g className="lg-box lg-box--3">
                  <rect x="26" y="104" width="52" height="44" rx="3" />
                  <g className="lg-tag"><rect x="26" y="87" width="80" height="16" rx="4" /><text x="33" y="98.5">Déboîtement</text></g>
                </g>
              </svg>

              <div className="lg-status">
                <span className="lg-status-dot"></span>
                <span className="lg-status-text">Analyse en cours</span>
                <span className="lg-progress"><span></span></span>
              </div>
            </div>

            <ul className="features-list">
              {['Détection automatique des dommages', 'Analyse en temps réel', 'Rapports détaillés'].map((f, i) => (
                <li className="feature" key={f} style={{ '--i': i }}>
                  <span className="feature-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Panneau droit : formulaire */}
        <main className="right-panel">
          <div className="login-container">
            <div className="login-header">
              <div className="logo-home" aria-hidden="true">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 8V5a2 2 0 0 1 2-2h3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M8 21H5a2 2 0 0 1-2-2v-3"/>
                  <circle cx="12" cy="12" r="3.2"/>
                </svg>
              </div>
              <h2>Connexion</h2>
              <p className="login-subtitle">Accédez à votre espace d'analyse</p>
            </div>

            <form onSubmit={handleSubmit} className="login-form" noValidate>
              <div className="form-group">
                <label htmlFor="username" className="login-label">Nom d'utilisateur</label>
                <div className="input-container">
                  <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    className={`form-control ${missingUser ? 'is-invalid' : ''}`}
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="Votre identifiant"
                    autoComplete="username"
                    autoFocus
                    aria-invalid={missingUser}
                    aria-describedby={missingUser ? 'username-error' : undefined}
                  />
                </div>
                {missingUser && <FieldError id="username-error">Le nom d'utilisateur est requis</FieldError>}
              </div>

              <div className="form-group">
                <label htmlFor="password" className="login-label">Mot de passe</label>
                <div className="input-container">
                  <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    className={`form-control has-toggle ${missingPass ? 'is-invalid' : ''}`}
                    value={formData.password}
                    onChange={handleInputChange}
                    onKeyUp={(e) => setCapsLock(e.getModifierState && e.getModifierState('CapsLock'))}
                    onBlur={() => setCapsLock(false)}
                    placeholder="Votre mot de passe"
                    autoComplete="current-password"
                    aria-invalid={missingPass}
                    aria-describedby={missingPass ? 'password-error' : undefined}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    )}
                  </button>
                </div>
                {capsLock && <div className="login-hint">Verrouillage majuscules activé</div>}
                {missingPass && <FieldError id="password-error">Le mot de passe est requis</FieldError>}
              </div>

              {error && (
                <div className="alert-danger" role="alert">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className={`btn-primary ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner" aria-hidden="true"></span>
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </button>
            </form>

            <p className="login-footer">© {new Date().getFullYear()} VeoSmart · CheckAI</p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Login;